// app/javascript/controllers/ui/manifesto/sticky_horizontal_controller.js

import { Controller } from "@hotwired/stimulus"

/**
 * Sticky Horizontal Controller
 *
 * Горизонтальный scroll внутри sticky-viewport.
 * Пока секция в sticky-состоянии, внутренний .mf-sticky__track скроллится
 * по горизонтали пропорционально вертикальному прогрессу.
 *
 * Высота самой секции задаётся через data-scroll-distance-value (кратно vh).
 * При scroll-distance=300 секция занимает 300vh пространства и отдаёт
 * (totalTrackW - viewportW) пикселей горизонтального смещения.
 *
 * @usedby _manifesto.html.erb
 *
 * Layout (ERB):
 *   <div class="mf-sticky"
 *        data-controller="ui--manifesto--sticky-horizontal"
 *        data-ui--manifesto--sticky-horizontal-distance-value="3">
 *     <div class="mf-sticky__viewport">
 *       <div class="mf-sticky__track">
 *         <article class="mf-sticky__panel">...</article>
 *         <article class="mf-sticky__panel">...</article>
 *         <article class="mf-sticky__panel">...</article>
 *       </div>
 *     </div>
 *   </div>
 *
 * Поведение при prefers-reduced-motion: секция раскладывается вертикально
 * (CSS отвечает через media query), контроллер выходит.
 */
export default class extends Controller {
  static targets = ["track", "viewport", "progress"]
  static values = {
    distance: { type: Number, default: 3 }   // section height = distance * 100vh
  }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    if (window.matchMedia("(max-width: 860px)").matches) return   // mobile: vertical stack

    this.element.style.height = `${this.distanceValue * 100}vh`

    // Prepare track: width = sum of panel widths
    this.#measure()

    this.onScroll = this.#onScroll.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })

    this.resizeObserver = new ResizeObserver(() => this.#measure())
    this.resizeObserver.observe(this.element)

    this.#onScroll()   // initial
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    this.element.style.height = ""
  }

  #measure() {
    if (!this.hasTrackTarget) return
    const track = this.trackTarget
    this.trackWidth = track.scrollWidth
    this.viewportWidth = window.innerWidth
    this.translateMax = Math.max(0, this.trackWidth - this.viewportWidth)
  }

  #onScroll() {
    if (!this.hasTrackTarget) return
    const rect = this.element.getBoundingClientRect()
    const total = this.element.offsetHeight - window.innerHeight
    if (total <= 0) return
    // progress 0..1
    const p = Math.max(0, Math.min(1, -rect.top / total))
    const x = -p * this.translateMax
    this.trackTarget.style.transform = `translate3d(${x}px, 0, 0)`

    if (this.hasProgressTarget) {
      this.progressTarget.style.transform = `scaleX(${p})`
    }
  }
}
